import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Overline, Reveal } from "@/lib/reveal";

const inputClass =
  "w-full rounded-sm border border-ivory/15 bg-ivory/[0.04] px-4 py-3.5 text-base text-ivory placeholder:text-stone/50 transition-colors duration-300 focus:border-gold/60 focus:bg-ivory/[0.06] focus:outline-none";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Contact() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(data.get("name") || ""),
          email: String(data.get("email") || ""),
          message: String(data.get("message") || ""),
        }),
      });
      if (!res.ok) throw new Error("send failed");
      form.reset();
      setSent(true);
      toast("Thank you — your question is on its way to April.", {
        description: "Replies come from april@theinkwelltherapy.com.",
      });
    } catch {
      toast("Something interrupted the send.", {
        description:
          "Please email april@theinkwelltherapy.com directly — your words matter.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      aria-labelledby="contact-heading"
      className="relative bg-charcoal py-24 sm:py-32"
    >
      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline index="06" label="Questions" />
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                id="contact-heading"
                className="mt-8 font-serif text-3xl font-light leading-tight tracking-tight text-ivory sm:text-5xl"
              >
                A conversation can
                <br />
                <em className="italic text-gold">begin with a question.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-8 max-w-md space-y-5 text-base leading-relaxed text-stone/80">
                <p>
                  Questions about therapy, fit, scheduling, insurance, or
                  private pay are welcome.
                </p>
                <p>
                  <a
                    href="mailto:april@theinkwelltherapy.com"
                    data-testid="contact-email-link"
                    className="text-ivory underline decoration-ivory/30 underline-offset-4 transition-colors hover:text-haint"
                  >
                    april@theinkwelltherapy.com
                  </a>
                  <br />
                  <a
                    href="tel:+15107300296"
                    data-testid="contact-phone-link"
                    className="text-ivory underline decoration-ivory/30 underline-offset-4 transition-colors hover:text-haint"
                  >
                    510.730.0296
                  </a>
                </p>
                <p className="text-sm text-stone/60">
                  Please do not include private, urgent, or clinical information
                  in this form or in email. If you need support now, call or
                  text 988.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              aria-label="Contact form"
              className="grid gap-5 sm:grid-cols-2"
            >
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] tracking-[0.22em] uppercase text-stone/70">
                  Name
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder="What should we call you?"
                  data-testid="contact-name-input"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] tracking-[0.22em] uppercase text-stone/70">
                  Email
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="Where can we reply?"
                  data-testid="contact-email-input"
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] tracking-[0.22em] uppercase text-stone/70">
                  Your question
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  required
                  placeholder="A few honest lines are plenty."
                  data-testid="contact-message-input"
                  className={`${inputClass} resize-y`}
                />
              </div>
              <div className="flex flex-wrap items-center gap-5 sm:col-span-2">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit-button"
                  className="rounded-sm border border-gold px-8 py-4 font-mono text-xs tracking-[0.22em] uppercase text-gold transition-colors duration-300 hover:bg-gold hover:text-inkblack disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send your question"}
                </button>
                {sent && (
                  <p data-testid="contact-sent-note" className="text-sm text-stone/70">
                    Received with thanks — a reply will follow.
                  </p>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
